import { accessToken } from '../auth';
import { config } from '../env';
import { shrinkPhoto } from './shrink';

export type PhotoKind = 'RECEIPT' | 'METER_READING' | 'DOCUMENT';
export type UploadedPhoto = { id: string; kind: PhotoKind; url: string };

/**
 * Receipts and documents can be PDFs (arqam docs 49, 52), up to 10 MB, sent
 * as they are.
 */
export const PDF_MAX_BYTES = 10 * 1024 * 1024;

export const isPdf = (file: { type?: string; name?: string }) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '');

/** An upload refused or interrupted: `code` is arqam's (doc 46), or NETWORK. */
export class PhotoUploadError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

/** arqam's upload errors (doc 46), in Arabic. */
export function photoErrorMessage(error: unknown): string {
  const code = error instanceof PhotoUploadError ? error.code : 'UNKNOWN';
  switch (code) {
    case 'PHOTO_TOO_LARGE':
      return 'الصورة كبيرة جداً';
    case 'PDF_TOO_LARGE':
      return 'ملف PDF أكبر من 10 ميغابايت. أرسل صوراً بدلاً منه.';
    case 'PDF_NOT_ALLOWED':
      return 'صورة العداد يجب أن تكون صورة، لا ملف PDF.';
    case 'NOT_AN_IMAGE':
      return 'الملف ليس صورة';
    case 'NETWORK':
      return 'انقطع الاتصال أثناء إرسال الصورة. أعد المحاولة.';
    case 'UNAUTHENTICATED':
      return 'انتهت الجلسة. أعد تحميل الصفحة وسجّل الدخول.';
    case 'FORBIDDEN':
      return 'لا تملك صلاحية إرسال الصور.';
    case 'PHOTOS_NOT_CONFIGURED':
      return 'حفظ الصور غير مفعّل في الخادم بعد. اتصل بالمسؤول.';
    case 'READ_FAILED':
      return 'تعذّرت قراءة الصورة. جرّب صورة أخرى.';
    default:
      return 'تعذّر إرسال الصورة. أعد المحاولة.';
  }
}

/**
 * `POST {API}/photos?kind=…` with the image bytes (not multipart). XHR rather
 * than fetch: only XHR reports upload progress, which matters on a slow
 * connection. `onProgress` gets 0–1.
 */
export async function uploadPhoto(
  photo: Blob,
  kind: PhotoKind,
  onProgress?: (fraction: number) => void,
): Promise<UploadedPhoto> {
  const url = new URL('/photos', config.graphqlUrl);
  url.searchParams.set('kind', kind);
  const token = await accessToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', photo.type || 'image/jpeg');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new PhotoUploadError('NETWORK', 0));
    xhr.ontimeout = () => reject(new PhotoUploadError('NETWORK', 0));
    xhr.onload = () => {
      let body: { id?: string; kind?: PhotoKind; url?: string; code?: string } =
        {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // not JSON (e.g. a proxy's 413 page)
      }
      if (xhr.status === 201 && body.id && body.url) {
        resolve({ id: body.id, kind: body.kind ?? kind, url: body.url });
      } else {
        const code =
          body.code ?? (xhr.status === 413 ? 'PHOTO_TOO_LARGE' : 'UNKNOWN');
        reject(new PhotoUploadError(code, xhr.status));
      }
    };
    xhr.send(photo);
  });
}

/** Who may send a PDF: receipts and documents, not meter photos. */
export const allowsPdf = (kind: PhotoKind) => kind !== 'METER_READING';

/**
 * A chosen file sent to arqam: a PDF as it is (≤ 10 MB), an image shrunk
 * first (as `PhotoField` does). For forms with several files (documents).
 */
export async function uploadFile(
  file: File,
  kind: PhotoKind,
  onProgress?: (fraction: number) => void,
): Promise<UploadedPhoto & { pdf: boolean }> {
  if (isPdf(file)) {
    if (!allowsPdf(kind)) throw new PhotoUploadError('PDF_NOT_ALLOWED', 0);
    if (file.size > PDF_MAX_BYTES) {
      throw new PhotoUploadError('PDF_TOO_LARGE', 0);
    }
    const pdf = new Blob([file], { type: 'application/pdf' });
    return { ...(await uploadPhoto(pdf, kind, onProgress)), pdf: true };
  }
  let blob: Blob;
  try {
    blob = await shrinkPhoto(file);
  } catch {
    throw new PhotoUploadError('READ_FAILED', 0);
  }
  return { ...(await uploadPhoto(blob, kind, onProgress)), pdf: false };
}
